<div class="space-y-4">
  <app-breadcrumbs [items]="breadcrumbItems()" />

  <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
    <div class="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
      <div>
        <h1 class="text-xl font-semibold text-slate-900">Client Profile</h1>
        <p class="mt-1 text-sm text-slate-500">{{ displayName() }}</p>
      </div>

      <button
        type="button"
        (click)="goBackToClient()"
        class="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        Back to Client
      </button>
    </div>

    @if (loading()) {
      <div class="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        Loading client profile...
      </div>
    } @else if (errorMessage()) {
      <div class="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {{ errorMessage() }}
      </div>
    } @else {
      @if (!isAdmin()) {
        <div class="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Read-only profile view. Only admins can edit client profile fields.
        </div>
      }

      <form [formGroup]="form" (ngSubmit)="submit()" class="mt-5 space-y-5">
        <div class="grid gap-4 sm:grid-cols-2">
          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Client Unique ID</label>
            <input type="text" formControlName="clientUniqueId" [readonly]="true" class="w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none" />
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Enrollment Date</label>
            <input type="date" formControlName="enrollmentDate" [readonly]="true" class="w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none" />
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">First Name</label>
            <input type="text" formControlName="firstName" [readonly]="!isAdmin()" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Middle Name</label>
            <input type="text" formControlName="middleName" [readonly]="!isAdmin()" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Last Name</label>
            <input type="text" formControlName="lastName" [readonly]="!isAdmin()" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Birthdate</label>
            <input type="date" formControlName="birthdate" [readonly]="!isAdmin()" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
          </div>

          <div class="sm:col-span-2">
            <label class="mb-1 block text-sm font-medium text-slate-700">Reason for Initial Contact</label>
            <textarea formControlName="reasonForInitialContact" rows="3" [readonly]="!isAdmin()" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"></textarea>
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Phone</label>
            <input type="text" formControlName="phone" [readonly]="!isAdmin()" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Email</label>
            <input type="email" formControlName="email" [readonly]="!isAdmin()" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Race</label>
            <select formControlName="race2020" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" [disabled]="!isAdmin()">
              <option value="">Select race</option>
              @for (option of raceOptions; track option) {
                <option [value]="option">{{ option }}</option>
              }
            </select>
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Ethnicity</label>
            <select formControlName="ethnicity2020" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" [disabled]="!isAdmin()">
              <option value="">Select ethnicity</option>
              @for (option of ethnicityOptions; track option) {
                <option [value]="option">{{ option }}</option>
              }
            </select>
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Gender</label>
            <select formControlName="gender" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" [disabled]="!isAdmin()">
              <option value="">Select gender</option>
              @for (option of genderOptions; track option) {
                <option [value]="option">{{ option }}</option>
              }
            </select>
          </div>

          @if (form.controls.gender.getRawValue() === 'Other') {
            <div>
              <label class="mb-1 block text-sm font-medium text-slate-700">Gender (Other)</label>
              <input type="text" formControlName="genderOtherText" [readonly]="!isAdmin()" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
            </div>
          }

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Zip Code</label>
            <input type="text" formControlName="zipCode" [readonly]="!isAdmin()" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Consent on File to Refer-Out</label>
            <select formControlName="consentOnFileToReferOut" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" [disabled]="!isAdmin()">
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          @if (form.controls.consentOnFileToReferOut.getRawValue() === 'yes') {
            <div>
              <label class="mb-1 block text-sm font-medium text-slate-700">Date of Consent</label>
              <input type="date" formControlName="consentOnFileToReferOutDate" [readonly]="!isAdmin()" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
            </div>
          }
        </div>

        @if (isAdmin()) {
          <div class="flex justify-end">
            <button
              type="submit"
              [disabled]="saving()"
              class="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {{ saving() ? 'Saving...' : 'Save Profile' }}
            </button>
          </div>
        }
      </form>
    }
  </section>
</div>
